import { Portal } from '@radix-ui/react-portal'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { Check, CircleAlert, X } from 'lucide-react'
import { useContext, useState } from 'react'
import { toast } from 'sonner'

import { getEnrollmentCoveragesQueryKey } from 'components/hooks/enrollments/useGetEnrollmentCoverages'
import { IconButton } from 'slab'
import { getGetBrokersUnprocessedEnrollmentsIdQueryKey, useGetBrokersMembersMemberIdCoveredParticipants } from 'utils/web_api'

import { type Coverage } from './types'
import {
  getEnrollmentEndDateOptions,
  getEnrollmentStartDateOptionsWithTooltip,
} from '../../utils'
import BrokersMemberContext from '../context/BrokersMemberContext'
import CoverageAuditReasonContext from '../context/CoverageAuditReasonContext'
import { useAddCoverageToEnrollment } from '../hooks/enrollments/useAddCoverageToEnrollment'
import { Banner } from '../reusable/Banner'
import LoadingSpinnerCentered from '../reusable/LoadingSpinnerCentered'
import { Button } from '../ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip'

export const BeforeEmployerCoverageStartDateTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <CircleAlert size={16} stroke='#EF4444' fill='#FECACA' />
      </TooltipTrigger>
      <Portal>
        <TooltipContent className='w-96' side='top' style={{ zIndex: 9999 }}>
          This date is before the employer coverage start date.
        </TooltipContent>
      </Portal>
    </Tooltip>
  </TooltipProvider>
)

const AddParticipantCoverageToEnrollment = ({
  enrollmentId,
  coverages,
}: {
  enrollmentId: string
  coverages: Coverage[] | undefined
}) => {
  const queryClient = useQueryClient()
  const { memberId, planYear, employerIchraConfig } =
    useContext(BrokersMemberContext)
  const { reason, openReasonDropdown } = useContext(CoverageAuditReasonContext)

  const {
    data: coveredParticipants,
    isLoading,
    isError,
  } = useGetBrokersMembersMemberIdCoveredParticipants(
    memberId,
    {
      plan_year: planYear || 0,
      include_pending: true,
    },
    {
      query: {
        enabled: !!memberId && !!planYear,
      },
    }
  )

  // Handle the fact that the generated API might return a single object or array
  const [selectedParticipant, setSelectedParticipant] = useState<
    string | undefined
  >()
  const [startDate, setStartDate] = useState<string | undefined>()
  const [endDate, setEndDate] = useState<string | undefined>()
  const [addParticipantError, setAddParticipantError] = useState<string>()
  const [isAddParticipantOpen, setIsAddParticipantOpen] = useState(false)

  const participantsWithCoverages =
    coverages?.map((c) => c.covered_participant_id) || []

  const coveredParticipantOptions =
    coveredParticipants
      ?.map((cp) => ({
        value: cp.id,
        label: `${cp.given_name} ${cp.family_name}`,
      }))
      .filter(({ value }) => !participantsWithCoverages.includes(value)) || []

  const isDisabled = !selectedParticipant || !startDate || !endDate
  const hasNoReason = !reason

  const handleClearParticipant = () => {
    setSelectedParticipant(undefined)
    setStartDate(undefined)
    setEndDate(undefined)
    setIsAddParticipantOpen(false)
  }

  const addCoverageToEnrollmentMutation = useAddCoverageToEnrollment({
    enrollmentId,
    memberId,
  })

  const handleAddParticipant = () => {
    if (!selectedParticipant || !startDate || !endDate) return
    addCoverageToEnrollmentMutation.mutate(
      {
        covered_participant_id: selectedParticipant,
        start_date: startDate,
        end_date: endDate,
        coverage_audit_reason: reason,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetBrokersUnprocessedEnrollmentsIdQueryKey(enrollmentId),
          })
          queryClient.invalidateQueries({
            queryKey: getEnrollmentCoveragesQueryKey({
              enrollmentId,
              memberId,
            }),
          })
          handleClearParticipant()
          toast.success('Successfully added participant to enrollment', {
            position: 'top-center',
          })
        },
        onError: (error) => {
          setAddParticipantError(
            `Error adding participant to enrollment: ${error.message}`
          )
        },
      }
    )
  }

  if (isLoading || !planYear) {
    return (
      <LoadingSpinnerCentered />
    )
  }

  if (isError) {
    return (
      <div className='flex justify-center items-center w-full h-full'>
        <div className='bg-slate-50 p-4 rounded-lg text-sm grow'>
          <div className='font-semibold text-red-500'>
            Error fetching covered participants
          </div>
        </div>
      </div>
    )
  }

  if (!isAddParticipantOpen) {
    const isAddParticipantDisabled = coveredParticipantOptions.length === 0
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={clsx({
                'cursor-not-allowed w-fit': isAddParticipantDisabled,
              })}
            >
              <Button
                variant='outline'
                onClick={() => {
                  if (hasNoReason) {
                    openReasonDropdown?.()
                    return
                  }
                  if (!isAddParticipantDisabled) {
                    setIsAddParticipantOpen(true)
                  }
                }}
                disabled={isAddParticipantDisabled}
              >
                Add participant
              </Button>
            </div>
          </TooltipTrigger>
          {isAddParticipantDisabled && (
            <TooltipContent className='w-96'>
              All existing covered participants have been added to this
              enrollment. Please add more participants before updating
              coverages.
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <div className='space-y-2'>
      <div className='text-lg font-semibold'>Add participant to enrollment</div>
      <div className='text-sm font-light'>
        Select a participant, then choose the coverages below.
      </div>
      {addParticipantError && (
        <Banner
          type='critical'
          titleText='Error adding participant to enrollment'
          className='my-2'
        >
          {addParticipantError}
        </Banner>
      )}
      <Select
        onValueChange={setSelectedParticipant}
        value={selectedParticipant}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {coveredParticipantOptions.map(({ value, label }, index) => (
            <SelectItem key={index} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selectedParticipant && (
        <div className='flex justify-between items-center'>
          <Select onValueChange={setStartDate} value={startDate}>
            <SelectTrigger className='w-[40%]'>
              <SelectValue placeholder='Start date' />
            </SelectTrigger>
            <SelectContent>
              {getEnrollmentStartDateOptionsWithTooltip(
                planYear,
                employerIchraConfig,
                <BeforeEmployerCoverageStartDateTooltip />
              ).map(({ value, label, tag }, index) => (
                <SelectItem key={index} value={value}>
                  <div className='flex items-center'>
                    <div>{label}</div>
                    <div className='ml-1'>{tag}</div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select onValueChange={setEndDate} value={endDate}>
            <SelectTrigger className='w-[40%]'>
              <SelectValue placeholder='End date' />
            </SelectTrigger>
            <SelectContent>
              {getEnrollmentEndDateOptions(planYear).map(
                ({ value, label }, index) => (
                  <SelectItem key={index} value={value}>
                    {label}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
          <div className='w-[16%] flex justify-end items-center space-x-1'>
            <IconButton
              disabled={isDisabled}
              onClick={handleAddParticipant}
              icon={Check}
              label='Add participant'
              variant='primary'
              size='large'
            />
            <IconButton
              disabled={false}
              onClick={handleClearParticipant}
              icon={X}
              label='Clear participant'
              variant='secondary'
              size='large'
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default AddParticipantCoverageToEnrollment
